import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Activity, Pause, Play, Trash2, Wifi, WifiOff } from 'lucide-react';
import { camerasApi, analyticsApi } from '../services/api';
import { useAuthStore } from '../services/auth';
import type { LiveFeedEvent } from '../types';

function getGenderColor(gender: string) {
  return gender === 'male' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' : 'bg-pink-100 dark:bg-pink-900/30 text-pink-800 dark:text-pink-300';
}

function getExpressionColor(expression: string) {
  const colors: Record<string, string> = {
    neutral: 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200',
    happy: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
    sad: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300',
    angry: 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300',
    fearful: 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300',
    disgusted: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
    surprised: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
  };
  return colors[expression] || 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200';
}

function formatTime(timestamp: string) {
  const d = new Date(timestamp);
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  const ago = formatRelativeTime(d);
  return `${time} (${ago})`;
}

function formatRelativeTime(date: Date) {
  const seconds = Math.round((Date.now() - date.getTime()) / 1000);
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

function shortenFaceId(faceId: string | null) {
  if (!faceId) return 'N/A';
  // Show last 6 chars to match camera overlay and Faces page
  return '#' + faceId.slice(-6);
}

function getWsUrl(): string {
  const apiBase = import.meta.env.VITE_API_BASE_URL || '/api';
  // Convert HTTP API URL to WebSocket URL
  // e.g. "https://example.com/api" -> "wss://example.com/ws"
  // or "/api" (relative) -> derive from window.location
  if (apiBase.startsWith('http')) {
    const url = new URL(apiBase);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    url.pathname = '/ws';
    return url.toString();
  }
  // Relative URL - derive from current page
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws`;
}

// Auto-incrementing ID for WebSocket events (negative to avoid collision with DB ids)
let wsEventCounter = -1;

export default function LiveFeed() {
  const [isPaused, setIsPaused] = useState(false);
  const [events, setEvents] = useState<LiveFeedEvent[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<string>('all');
  const [wsConnected, setWsConnected] = useState(false);
  const lastSeenIdRef = useRef<number>(0);
  const feedRef = useRef<HTMLDivElement>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const token = useAuthStore((s) => s.token);

  const { data: cameras } = useQuery({
    queryKey: ['cameras'],
    queryFn: camerasApi.list,
  });

  const cameraIds = cameras?.map((c) => c.id) || [];
  const filteredCameraIds = selectedCamera === 'all' ? cameraIds : [selectedCamera];

  // WebSocket connection for real-time events
  const connectWs = useCallback(() => {
    if (!token) return;

    const ws = new WebSocket(getWsUrl());
    wsRef.current = ws;

    ws.onopen = () => {
      // Register as dashboard with JWT token
      ws.send(JSON.stringify({
        type: 'register_dashboard',
        data: { token },
      }));
      setWsConnected(true);
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'live_feed_event' && msg.data && !isPaused) {
          const wsEvent: LiveFeedEvent = {
            id: wsEventCounter--,
            camera_id: msg.data.camera_id,
            camera_name: msg.data.camera_name,
            timestamp: msg.data.timestamp,
            age_group: msg.data.age_group,
            gender: msg.data.gender,
            gender_confidence: msg.data.gender_confidence,
            expression: msg.data.expression,
            expression_confidence: msg.data.expression_confidence,
            attention_duration_ms: null,
            face_id: msg.data.face_id,
          };

          // Apply camera filter
          if (selectedCamera !== 'all' && wsEvent.camera_id !== selectedCamera) return;

          setEvents((prev) => [wsEvent, ...prev].slice(0, 500));
        }
      } catch {
        // Ignore unparseable messages (e.g. "connected", "pong")
      }
    };

    ws.onclose = () => {
      setWsConnected(false);
      wsRef.current = null;
      // Reconnect after 3 seconds
      reconnectTimerRef.current = setTimeout(connectWs, 3000);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [token, isPaused, selectedCamera]);

  useEffect(() => {
    connectWs();
    return () => {
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [connectWs]);

  // Keep-alive ping every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping', data: null }));
      }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // HTTP polling as fallback - less frequent since WS provides real-time
  const { data: newEvents } = useQuery({
    queryKey: ['live-feed', filteredCameraIds.join(',')],
    queryFn: () => analyticsApi.getLiveFeed(filteredCameraIds, 50),
    enabled: !isPaused && filteredCameraIds.length > 0,
    refetchInterval: wsConnected ? 10000 : 2000, // Poll less when WS is connected
  });

  // Merge polled events into the feed (dedup by face_id + timestamp)
  useEffect(() => {
    if (!newEvents || newEvents.length === 0) return;

    setEvents((prev) => {
      const newOnes = newEvents.filter((e) => e.id > lastSeenIdRef.current);
      if (newOnes.length === 0) return prev;

      lastSeenIdRef.current = Math.max(...newEvents.map((e) => e.id));

      // Merge: add polled events that aren't already shown via WS (dedup by face_id + close timestamp)
      const existingFaceTimestamps = new Set(
        prev.map((e) => `${e.face_id}_${e.timestamp.slice(0, 16)}`)
      );
      const genuinelyNew = newOnes.filter(
        (e) => !existingFaceTimestamps.has(`${e.face_id}_${e.timestamp.slice(0, 16)}`)
      );
      if (genuinelyNew.length === 0) return prev;

      return [...genuinelyNew, ...prev].slice(0, 500);
    });
  }, [newEvents]);

  // Auto-scroll to top when new events arrive (if not paused)
  useEffect(() => {
    if (!isPaused && feedRef.current) {
      feedRef.current.scrollTop = 0;
    }
  }, [events, isPaused]);

  const uniqueFaceIds = new Set(events.map((e) => e.face_id).filter(Boolean));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Activity className="w-7 h-7 text-primary-500 dark:text-primary-400" />
            Live Detection Feed
          </h1>
          <p className="text-gray-500 dark:text-slate-400 mt-1">Real-time face detections as they happen</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={selectedCamera}
            onChange={(e) => { setSelectedCamera(e.target.value); setEvents([]); lastSeenIdRef.current = 0; }}
            className="px-3 py-2 border dark:border-slate-700 rounded-lg text-sm dark:bg-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Cameras</option>
            {cameras?.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <button
            onClick={() => setIsPaused(!isPaused)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              isPaused
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-yellow-500 text-white hover:bg-yellow-600'
            }`}
          >
            {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
            {isPaused ? 'Resume' : 'Pause'}
          </button>
          <button
            onClick={() => { setEvents([]); lastSeenIdRef.current = 0; }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Clear
          </button>
        </div>
      </div>

      {/* Summary bar */}
      <div className="grid grid-cols-4 gap-4 mb-4">
        <div className="bg-white dark:bg-slate-800 rounded-lg border dark:border-slate-700 p-4">
          <div className="text-sm text-gray-500 dark:text-slate-400">Events Shown</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{events.length}</div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-lg border dark:border-slate-700 p-4">
          <div className="text-sm text-gray-500 dark:text-slate-400">Unique Faces</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{uniqueFaceIds.size}</div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-lg border dark:border-slate-700 p-4">
          <div className="text-sm text-gray-500 dark:text-slate-400">Status</div>
          <div className="text-2xl font-bold flex items-center gap-2">
            {isPaused ? (
              <span className="text-yellow-600">Paused</span>
            ) : wsConnected ? (
              <>
                <Wifi className="w-5 h-5 text-green-500" />
                <span className="text-green-600">Live</span>
              </>
            ) : (
              <>
                <WifiOff className="w-5 h-5 text-orange-400" />
                <span className="text-orange-500">Polling</span>
              </>
            )}
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-lg border dark:border-slate-700 p-4">
          <div className="text-sm text-gray-500 dark:text-slate-400">Latest</div>
          <div className="text-lg font-bold text-gray-900 dark:text-white">
            {events.length > 0 ? formatTime(events[0].timestamp) : '--:--:--'}
          </div>
        </div>
      </div>

      {/* Feed table */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border dark:border-slate-700 shadow-sm">
        <div className="overflow-x-auto" ref={feedRef} style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-slate-900 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-slate-300">Time</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-slate-300">Camera</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-slate-300">Face ID</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-slate-300">Gender</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-slate-300">Age Group</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-slate-300">Expression</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-slate-300">Confidence</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
              {events.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-400 dark:text-slate-500">
                    {filteredCameraIds.length === 0
                      ? 'No cameras found. Add a camera first.'
                      : 'Waiting for detections...'}
                  </td>
                </tr>
              )}
              {events.map((event) => (
                <tr key={event.id} className="hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                  <td className="px-4 py-2 font-mono text-gray-700 dark:text-slate-200 whitespace-nowrap">
                    {formatTime(event.timestamp)}
                  </td>
                  <td className="px-4 py-2 text-gray-700 dark:text-slate-200">{event.camera_name}</td>
                  <td className="px-4 py-2">
                    <span className="font-mono text-xs bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300 px-2 py-1 rounded" title={event.face_id || 'No face ID'}>
                      {shortenFaceId(event.face_id)}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getGenderColor(event.gender)}`}>
                      {event.gender}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-gray-700 dark:text-slate-200 font-medium">{event.age_group}</td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getExpressionColor(event.expression)}`}>
                      {event.expression}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-gray-500 dark:text-slate-400">
                    <div className="flex items-center gap-2">
                      <div className="w-16 bg-gray-200 dark:bg-slate-600 rounded-full h-1.5">
                        <div
                          className="bg-primary-500 h-1.5 rounded-full"
                          style={{ width: `${Math.round(event.gender_confidence * 100)}%` }}
                        />
                      </div>
                      <span className="text-xs">{Math.round(event.gender_confidence * 100)}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
